<template>
  <div
    class="modal fade"
    id="AddConfigurationModal"
    tabindex="-1"
    role="dialog"
    ref="addConfigurationModalRef"
    aria-labelledby="AddConfigurationModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="AddConfigurationModalLabel">{{ title }}</h4>
          <button
            class="btn-close py-0"
            type="button"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <Form ref="configurationForm" @submit="addConfiguration" :validation-schema="configurationSchema">
            <div class="row">

              <!-- Clé -->
              <div class="col-md-12 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Clé <span class="text-danger">*</span>
                  </label>
                  <Field
                    name="cle"
                    type="text"
                    class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer la clé de configuration"
                    :disabled="isUPDATE"
                  />
                  <ErrorMessage name="cle" class="text-danger" />
                </div>
              </div>

              <!-- Valeur (toggle boolean) -->
              <div class="col-md-12 mb-3">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Valeur <span class="text-danger">*</span>
                  </label>
                  <div class="form-check form-switch">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="valeurSwitch"
                      v-model="currentValeur"
                    />
                    <label class="form-check-label ms-2" for="valeurSwitch">
                      {{ valeurLabel }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="col-md-12">
                <button class="btn btn-primary w-100" type="submit">
                  {{ btntext }}
                </button>
              </div>

            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, watch, computed } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { error, hideModal, success } from '@/utils/utils';
import { Configuration } from '@/models/Configuration';

export default {
  name: 'AddConfigurationModal',
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  props: {
    id: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const configurationSchema = Yup.object().shape({
      cle: Yup.string().required('La clé est obligatoire'),
    });

    const configurationForm = ref<InstanceType<typeof Form> | null>(null);
    const addConfigurationModalRef = ref<null | HTMLElement>(null);
    const title = ref('Ajouter une configuration');
    const btntext = ref('Ajouter');
    const isUPDATE = ref(false);
    const currentValeur = ref(false);

    const valeurLabel = computed(() => (currentValeur.value ? 'Activé' : 'Désactivé'));

    const btnTitle = () => {
      if (isUPDATE.value) {
        title.value = 'Modifier une configuration';
        btntext.value = 'Modifier';
      } else {
        title.value = 'Ajouter une configuration';
        btntext.value = 'Ajouter';
      }
    };

    const getConfiguration = async (id: number) => {
      return ApiService.get('/configuration/' + id)
        .then(({ data }) => {
          const config: Configuration = data.data;
          configurationForm.value?.setFieldValue('id', config.id);
          configurationForm.value?.setFieldValue('cle', config.cle);
          configurationForm.value?.setFieldValue('valeur', config.valeur);
          currentValeur.value = config.valeur;
          emit('openmodal', addConfigurationModalRef.value);
        })
        .catch(({ response }) => {
          error(response?.data?.message ?? 'Erreur lors du chargement');
        });
    };

    watch(
      () => props.id,
      (newValue) => {
        if (newValue !== 0) {
          isUPDATE.value = true;
          btnTitle();
          getConfiguration(newValue);
        }
      }
    );

    const addConfiguration = async (values: any, { resetForm }: any) => {
      // Injecter la valeur du toggle directement
      values.valeur = currentValeur.value;

      if (isUPDATE.value) {
        ApiService.put('/configuration/' + values.id, { valeur: values.valeur })
          .then(({ data }) => {
            if (data.code == 200) {
              success(data.message);
              resetForm();
              hideModal(addConfigurationModalRef.value);
              isUPDATE.value = false;
              btnTitle();
              emit('close');
            }
          })
          .catch(({ response }) => {
            error(response?.data?.message ?? 'Erreur lors de la modification');
          });
      } else {
        ApiService.post('/configuration', values)
          .then(({ data }) => {
            if (data.code == 201) {
              success(data.message);
              resetForm();
              hideModal(addConfigurationModalRef.value);
              emit('close');
            }
          })
          .catch(({ response }) => {
            error(response?.data?.message ?? 'Erreur lors de la création');
          });
      }
    };

    const resetValue = () => {
      isUPDATE.value = false;
      currentValeur.value = false;
      btnTitle();
    };

    return {
      configurationSchema,
      configurationForm,
      addConfigurationModalRef,
      title,
      btntext,
      isUPDATE,
      currentValeur,
      valeurLabel,
      addConfiguration,
      resetValue,
    };
  },
};
</script>
